import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  App,
  Badge,
  Button,
  Col,
  DatePicker,
  Empty,
  Input,
  Result,
  Row,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  CopyOutlined,
  HistoryOutlined,
  ReloadOutlined,
  SaveOutlined,
  SearchOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Link, useNavigate } from 'react-router-dom';
import { useEodCloseMutation, useEodEligibleStages } from '@/hooks/useEodClose';
import { useUsers, useWorkstations } from '@/hooks/useQCWorkflow';
import { useAuthStore } from '@/store/authStore';
import { getQcContextCompanyId } from '@/services/qc-workflow.service';
import EodOperationWorkLogDrawer from '@/components/manufacturing/eod-close/EodOperationWorkLogDrawer';
import {
  applyRegisterPatch,
  buildRegisterBatchItems,
  canAccessEodClose,
  copyFromLastProgress,
  createRegisterRow,
  eodBoqLineLabel,
  eodDraftStorageKey,
  isBackEntryStage,
  mergeDraftRows,
  needsAssignmentForm,
  parseEodDraft,
  registerOutcomeCounts,
  rowRequiresQty,
  serializeEodDraft,
  todayIsoDate,
  validateRegisterRow,
  validateWorkDate,
  type EodCloseOutcome,
  type EodEligibleStage,
  type EodRegisterPatch,
  type EodRegisterRow,
} from '@/utils/eodClose';

const { Title, Text } = Typography;

const OUTCOME_OPTIONS: { label: string; value: EodCloseOutcome }[] = [
  { label: 'Done', value: 'COMPLETE' },
  { label: 'Pause', value: 'PAUSE' },
  { label: 'Cont.', value: 'IN_PROGRESS' },
];

const OUTCOME_LABEL: Record<EodCloseOutcome, string> = {
  COMPLETE: 'Complete',
  PAUSE: 'Pause',
  IN_PROGRESS: 'In Progress',
};

const REGISTER_FILTERS = { include_completed: true } as const;

const STATUS_FILTER_OPTIONS = [
  { value: 'Pending', label: 'Pending' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Paused', label: 'Paused' },
  { value: 'Completed', label: 'Completed (back entry)' },
];

type RowResult = { ok: boolean; error?: string; warning?: string; duplicate?: boolean };
type Option = { value: string; label: string };

function userLabel(u: { id?: string; full_name?: string; first_name?: string; last_name?: string; email?: string }): string {
  const full = u.full_name || [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || '';
  return full || (u.email || '').trim() || String(u.id || '');
}

function workstationLabel(w: { id: string; workstation_code?: string; workstation_name?: string; code?: string; name?: string }): string {
  const code = (w.workstation_code || w.code || '').trim();
  const name = (w.workstation_name || w.name || '').trim();
  if (code && name) return `${code} · ${name}`;
  return code || name || w.id;
}

function fmtQty(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 1000) / 1000);
}

/** Enter moves to the same column on the next row (quick keyboard entry). */
function focusNextRow(e: React.KeyboardEvent<HTMLElement>) {
  if (e.key !== 'Enter') return;
  const cell = (e.currentTarget as HTMLElement).getAttribute('data-eod-cell');
  if (!cell) return;
  const [col, idx] = cell.split(':');
  const next = document.querySelector<HTMLInputElement>(`[data-eod-cell="${col}:${Number(idx) + 1}"]`);
  if (next) {
    e.preventDefault();
    next.focus();
    next.select?.();
  }
}

type TextCellProps = {
  stageId: string;
  field: 'startTime' | 'endTime' | 'hours' | 'qty' | 'notes';
  value: string;
  cellId: string;
  onPatch: (stageId: string, patch: EodRegisterPatch) => void;
  type?: 'time' | 'text';
  inputMode?: 'decimal' | 'text';
  placeholder?: string;
  status?: 'error';
  width?: number | string;
};

const TextCell = memo(function TextCell(p: TextCellProps) {
  return (
    <Input
      size="small"
      type={p.type ?? 'text'}
      inputMode={p.inputMode}
      value={p.value}
      placeholder={p.placeholder}
      status={p.status}
      data-eod-cell={p.cellId}
      onKeyDown={focusNextRow}
      style={{ width: p.width ?? '100%' }}
      onChange={(e) => p.onPatch(p.stageId, { [p.field]: e.target.value } as EodRegisterPatch)}
    />
  );
});

type SelectCellProps = {
  stageId: string;
  field: 'operatorId' | 'workstationId' | 'inspectorId' | 'verifierId';
  value: string | null;
  options: Option[];
  onPatch: (stageId: string, patch: EodRegisterPatch) => void;
  placeholder: string;
  status?: 'error';
};

const SelectCell = memo(function SelectCell(p: SelectCellProps) {
  return (
    <Select
      size="small"
      showSearch
      allowClear
      virtual
      optionFilterProp="label"
      style={{ width: '100%' }}
      placeholder={p.placeholder}
      value={p.value || undefined}
      status={p.status}
      options={p.options}
      popupMatchSelectWidth={260}
      onChange={(v) => p.onPatch(p.stageId, { [p.field]: (v as string | undefined) ?? null } as EodRegisterPatch)}
    />
  );
});

const OutcomeCell = memo(function OutcomeCell(p: {
  stageId: string;
  value: EodCloseOutcome;
  onPatch: (stageId: string, patch: EodRegisterPatch) => void;
}) {
  return (
    <Segmented
      size="small"
      options={OUTCOME_OPTIONS}
      value={p.value}
      onChange={(v) => p.onPatch(p.stageId, { outcome: v as EodCloseOutcome })}
    />
  );
});

const EodClosePage: React.FC = () => {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const authUser = useAuthStore((s) => s.user);
  const allowed = canAccessEodClose(authUser?.permissions, Boolean(authUser?.is_super_admin));
  const companyId = getQcContextCompanyId();
  const userId = String(authUser?.id || '');

  const [workDate, setWorkDate] = useState<string>(() => todayIsoDate());
  const [search, setSearch] = useState('');
  const [woFilter, setWoFilter] = useState<string | undefined>();
  const [wsFilter, setWsFilter] = useState<string | undefined>();
  const [opFilter, setOpFilter] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [editedOnly, setEditedOnly] = useState(false);

  const [rows, setRows] = useState<Record<string, EodRegisterRow>>({});
  const [results, setResults] = useState<Record<string, RowResult>>({});
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [logStage, setLogStage] = useState<EodEligibleStage | null>(null);

  const [bulk, setBulk] = useState<{
    outcome?: EodCloseOutcome;
    startTime: string;
    endTime: string;
    hours: string;
    operatorId?: string;
    workstationId?: string;
    notes: string;
  }>({ startTime: '', endTime: '', hours: '', notes: '' });

  const { data: stages = [], isLoading, isFetching, error, refetch } = useEodEligibleStages(REGISTER_FILTERS, allowed);
  const { data: workstations = [] } = useWorkstations(true);
  const { data: users = [] } = useUsers({ is_active: true });
  const closeMutation = useEodCloseMutation();

  const draftKey = eodDraftStorageKey(userId, companyId, workDate);
  const draftLoadedFor = useRef<string | null>(null);

  const stagesById = useMemo(() => new Map(stages.map((s) => [s.id, s])), [stages]);

  // Rebuild rows when the stage list or the work date (draft) changes; keep unsaved edits.
  useEffect(() => {
    const loadDraft = draftLoadedFor.current !== draftKey;
    const draft = loadDraft ? parseEodDraft(localStorage.getItem(draftKey)) : [];
    setRows((prev) => mergeDraftRows(stages, loadDraft ? {} : prev, draft));
    if (loadDraft) {
      draftLoadedFor.current = draftKey;
      if (draft.length > 0) message.info(`Restored ${draft.length} unsaved row(s) for ${workDate}`);
    }
  }, [stages, draftKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save draft (dirty rows only)
  useEffect(() => {
    if (draftLoadedFor.current !== draftKey) return;
    const t = window.setTimeout(() => {
      const raw = serializeEodDraft(Object.values(rows));
      if (raw === '[]') localStorage.removeItem(draftKey);
      else localStorage.setItem(draftKey, raw);
    }, 400);
    return () => window.clearTimeout(t);
  }, [rows, draftKey]);

  const workstationOptions: Option[] = useMemo(
    () => workstations.map((w) => ({ value: w.id, label: workstationLabel(w) })),
    [workstations]
  );
  const userOptions: Option[] = useMemo(
    () => (users as Array<{ id: string }>).map((u) => ({ value: u.id, label: userLabel(u) })),
    [users]
  );
  const workstationDefaultOperator = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of workstations as Array<{ id: string; default_operator_user_id?: string | null }>) {
      if (w.default_operator_user_id) m.set(w.id, w.default_operator_user_id);
    }
    return m;
  }, [workstations]);

  const woOptions: Option[] = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of stages) m.set(s.work_order_id, s.work_order_number || s.work_order_id);
    return [...m.entries()].map(([value, label]) => ({ value, label }));
  }, [stages]);

  const patchRow = useCallback(
    (stageId: string, patch: EodRegisterPatch) => {
      setRows((prev) => {
        const row = prev[stageId];
        if (!row) return prev;
        const effective: EodRegisterPatch = { ...patch };
        if (patch.workstationId && !('operatorId' in patch)) {
          const def = workstationDefaultOperator.get(patch.workstationId);
          if (def) effective.operatorId = def;
        }
        return { ...prev, [stageId]: applyRegisterPatch(row, effective) };
      });
      setResults((prev) => {
        if (!prev[stageId]) return prev;
        const next = { ...prev };
        delete next[stageId];
        return next;
      });
    },
    [workstationDefaultOperator]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return stages.filter((s) => {
      if (woFilter && s.work_order_id !== woFilter) return false;
      if (statusFilter && s.status !== statusFilter) return false;
      const row = rows[s.id];
      if (wsFilter && (row?.workstationId ?? s.workstation_id) !== wsFilter) return false;
      if (opFilter && (row?.operatorId ?? s.assigned_to_user_id) !== opFilter) return false;
      if (editedOnly && !row?.dirty) return false;
      if (!q) return true;
      return [s.work_order_number, s.stage_code, s.stage_name, s.item_code, s.item_description]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [stages, rows, search, woFilter, wsFilter, opFilter, statusFilter, editedOnly]);

  const dirtyRows = useMemo(() => Object.values(rows).filter((r) => r.dirty && stagesById.has(r.stageId)), [rows, stagesById]);
  const selectedIds = selectedKeys.map(String).filter((id) => rows[id]);
  const workDateError = validateWorkDate(workDate);

  const applyBulk = () => {
    if (selectedIds.length === 0) return;
    const patch: EodRegisterPatch = {};
    if (bulk.outcome) patch.outcome = bulk.outcome;
    if (bulk.startTime) patch.startTime = bulk.startTime;
    if (bulk.endTime) patch.endTime = bulk.endTime;
    if (bulk.hours.trim()) patch.hours = bulk.hours.trim();
    if (bulk.workstationId) patch.workstationId = bulk.workstationId;
    if (bulk.operatorId) patch.operatorId = bulk.operatorId;
    if (bulk.notes.trim()) patch.notes = bulk.notes.trim();
    if (Object.keys(patch).length === 0) {
      message.warning('Fill at least one bulk field');
      return;
    }
    for (const id of selectedIds) patchRow(id, patch);
    message.success(`Applied to ${selectedIds.length} row(s)`);
  };

  const copyYesterday = () => {
    const targetIds =
      selectedIds.length > 0
        ? selectedIds
        : filtered.filter((s) => !rows[s.id]?.dirty).map((s) => s.id);
    const copyIds = targetIds.filter((id) => rows[id] && stagesById.get(id)?.last_progress);
    if (copyIds.length === 0) {
      message.info('No previous entries to copy for these rows');
      return;
    }
    setRows((prev) => {
      const next = { ...prev };
      for (const id of copyIds) {
        const s = stagesById.get(id);
        if (s && next[id]) next[id] = copyFromLastProgress(next[id], s);
      }
      return next;
    });
    message.success(`Copied last entry into ${copyIds.length} row(s)`);
  };

  const discardEdits = () => {
    modal.confirm({
      title: 'Discard unsaved rows?',
      content: `${dirtyRows.length} edited row(s) will be reset.`,
      okText: 'Discard',
      okButtonProps: { danger: true },
      onOk: () => {
        setRows(Object.fromEntries(stages.map((s) => [s.id, createRegisterRow(s)])));
        setResults({});
        localStorage.removeItem(draftKey);
      },
    });
  };

  const save = async () => {
    if (workDateError) {
      message.warning(workDateError);
      return;
    }
    if (dirtyRows.length === 0) {
      message.info('No edited rows to save');
      return;
    }
    const invalid: Record<string, RowResult> = {};
    const valid: EodRegisterRow[] = [];
    for (const r of dirtyRows) {
      const s = stagesById.get(r.stageId)!;
      const err = validateRegisterRow(r, s);
      if (err) invalid[r.stageId] = { ok: false, error: err };
      else valid.push(r);
    }
    if (Object.keys(invalid).length > 0) {
      setResults((prev) => ({ ...prev, ...invalid }));
    }
    if (valid.length === 0) {
      message.warning(`${Object.keys(invalid).length} row(s) need fixing before save`);
      return;
    }
    const backEntryCount = valid.filter((r) => isBackEntryStage(stagesById.get(r.stageId)!)).length;
    const counts = registerOutcomeCounts(valid.filter((r) => !isBackEntryStage(stagesById.get(r.stageId)!)));
    const totalHours = valid.reduce((sum, r) => sum + Number(r.hours), 0);
    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: `Save ${valid.length} row(s) for ${workDate}?`,
        content: (
          <Space direction="vertical" size={2}>
            <Text>
              Complete {counts.COMPLETE} · Pause {counts.PAUSE} · In Progress {counts.IN_PROGRESS}
              {backEntryCount > 0 ? ` · Back entry ${backEntryCount}` : ''}
            </Text>
            <Text>Total hours: {Math.round(totalHours * 100) / 100}</Text>
            {Object.keys(invalid).length > 0 ? (
              <Text type="warning">{Object.keys(invalid).length} row(s) with errors are skipped.</Text>
            ) : null}
            <Text type="secondary">Complete rows pass the stage inspection by EOD waiver.</Text>
            {backEntryCount > 0 ? (
              <Text type="secondary">Back entries only add to the work log; completed operations stay as they are.</Text>
            ) : null}
          </Space>
        ),
        okText: 'Save',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;

    try {
      const items = buildRegisterBatchItems(valid, stagesById, workDate);
      const res = await closeMutation.mutateAsync(items);
      const byStage: Record<string, RowResult> = {};
      for (const r of res.results) {
        byStage[r.stageId] = { ok: r.ok, error: r.error, warning: r.warning, duplicate: r.duplicate };
      }
      setResults((prev) => ({ ...prev, ...byStage }));
      setRows((prev) => {
        const next = { ...prev };
        for (const [stageId, r] of Object.entries(byStage)) {
          const s = stagesById.get(stageId);
          if (r.ok && s) next[stageId] = createRegisterRow(s);
        }
        return next;
      });
      setSelectedKeys([]);
      const okCount = res.results.filter((r) => r.ok).length;
      const failCount = res.results.length - okCount;
      if (failCount === 0) message.success(`Saved ${okCount} row(s)`);
      else message.warning(`Saved ${okCount} · ${failCount} failed — see the Status column`);
      await refetch();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
        (e as { message?: string })?.message ||
        'EOD save failed';
      message.error(msg);
    }
  };

  const indexById = useMemo(() => new Map(filtered.map((s, i) => [s.id, i])), [filtered]);

  const columns: ColumnsType<EodEligibleStage> = useMemo(
    () => [
      {
        title: 'Work order / operation',
        key: 'wo',
        fixed: 'left',
        width: 250,
        render: (_, s) => (
          <Space direction="vertical" size={0} style={{ lineHeight: 1.25, width: '100%', maxWidth: 230, overflow: 'hidden' }}>
            <Space size={4} wrap>
              <Link to={`/mfg/work-orders/${s.work_order_id}`}>
                <Text strong>{s.work_order_number || s.work_order_id}</Text>
              </Link>
              <Tag style={{ marginInlineEnd: 0 }}>{s.status}</Tag>
              {isBackEntryStage(s) && s.work_order_status ? (
                <Tooltip title="Work order status">
                  <Tag color="default" style={{ marginInlineEnd: 0 }}>
                    WO: {s.work_order_status}
                  </Tag>
                </Tooltip>
              ) : null}
              {s.inspection_required ? <Tag color="gold">Insp.</Tag> : null}
            </Space>
            <Text style={{ fontSize: 12 }}>
              {s.stage_code} — {s.stage_name}
              {s.production_unit_number != null ? ` · U${s.production_unit_number}` : ''}
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }} ellipsis={{ tooltip: eodBoqLineLabel(s) }}>
              {eodBoqLineLabel(s)}
            </Text>
          </Space>
        ),
      },
      {
        title: 'Outcome',
        key: 'outcome',
        width: 170,
        render: (_, s) => {
          const row = rows[s.id];
          if (!row) return null;
          if (isBackEntryStage(s)) {
            return (
              <Tooltip title="Operation is completed: the row is added to the work log only">
                <Tag color="purple">Back entry (log only)</Tag>
              </Tooltip>
            );
          }
          return <OutcomeCell stageId={s.id} value={row.outcome} onPatch={patchRow} />;
        },
      },
      {
        title: 'Start',
        key: 'start',
        width: 100,
        render: (_, s) => {
          const row = rows[s.id];
          return row ? (
            <TextCell stageId={s.id} field="startTime" type="time" value={row.startTime} cellId={`start:${indexById.get(s.id)}`} onPatch={patchRow} />
          ) : null;
        },
      },
      {
        title: 'End',
        key: 'end',
        width: 100,
        render: (_, s) => {
          const row = rows[s.id];
          return row ? (
            <TextCell stageId={s.id} field="endTime" type="time" value={row.endTime} cellId={`end:${indexById.get(s.id)}`} onPatch={patchRow} />
          ) : null;
        },
      },
      {
        title: 'Hours *',
        key: 'hours',
        width: 80,
        render: (_, s) => {
          const row = rows[s.id];
          return row ? (
            <TextCell
              stageId={s.id}
              field="hours"
              inputMode="decimal"
              value={row.hours}
              placeholder="0"
              cellId={`hours:${indexById.get(s.id)}`}
              onPatch={patchRow}
              status={row.dirty && !row.hours.trim() ? 'error' : undefined}
            />
          ) : null;
        },
      },
      {
        title: 'Qty today',
        key: 'qty',
        width: 130,
        render: (_, s) => {
          const row = rows[s.id];
          if (!row) return null;
          const needed = rowRequiresQty(row, s);
          return (
            <Space direction="vertical" size={0}>
              <TextCell
                stageId={s.id}
                field="qty"
                inputMode="decimal"
                value={row.qty}
                placeholder={needed ? 'required' : '—'}
                cellId={`qty:${indexById.get(s.id)}`}
                onPatch={patchRow}
                status={row.dirty && needed && !row.qty.trim() ? 'error' : undefined}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {fmtQty(s.completed_qty)} / {fmtQty(s.planned_quantity)} {s.uom || ''}
              </Text>
            </Space>
          );
        },
      },
      {
        title: 'Operator',
        key: 'operator',
        width: 170,
        render: (_, s) => {
          const row = rows[s.id];
          return row ? (
            <SelectCell
              stageId={s.id}
              field="operatorId"
              value={row.operatorId}
              options={userOptions}
              placeholder="Operator"
              onPatch={patchRow}
              status={row.dirty && !row.operatorId ? 'error' : undefined}
            />
          ) : null;
        },
      },
      {
        title: 'Workstation',
        key: 'workstation',
        width: 160,
        render: (_, s) => {
          const row = rows[s.id];
          return row ? (
            <SelectCell
              stageId={s.id}
              field="workstationId"
              value={row.workstationId}
              options={workstationOptions}
              placeholder="Workstation"
              onPatch={patchRow}
              status={row.dirty && !row.workstationId ? 'error' : undefined}
            />
          ) : null;
        },
      },
      {
        title: 'Notes *',
        key: 'notes',
        width: 220,
        render: (_, s) => {
          const row = rows[s.id];
          return row ? (
            <TextCell
              stageId={s.id}
              field="notes"
              value={row.notes}
              placeholder="Work done"
              cellId={`notes:${indexById.get(s.id)}`}
              onPatch={patchRow}
              status={row.dirty && !row.notes.trim() ? 'error' : undefined}
            />
          ) : null;
        },
      },
      {
        title: 'Status',
        key: 'result',
        width: 150,
        fixed: 'right',
        render: (_, s) => {
          const r = results[s.id];
          const row = rows[s.id];
          return (
            <Space size={4} align="start">
              {r ? (
                r.ok ? (
                  <Tooltip title={r.warning || (r.duplicate ? 'Already saved earlier' : undefined)}>
                    <Tag color={r.warning ? 'warning' : 'success'}>{r.duplicate ? 'Already saved' : 'Saved'}</Tag>
                  </Tooltip>
                ) : (
                  <Text type="danger" style={{ fontSize: 12 }}>
                    {r.error || 'Failed'}
                  </Text>
                )
              ) : row?.dirty ? (
                <Badge status="processing" text="Edited" />
              ) : null}
              <Tooltip title="Work log">
                <Button size="small" type="text" icon={<HistoryOutlined />} onClick={() => setLogStage(s)} />
              </Tooltip>
            </Space>
          );
        },
      },
    ],
    [rows, results, patchRow, userOptions, workstationOptions, indexById]
  );

  if (!allowed) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="403"
          title="Permission required"
          subTitle="You need manufacturing.wo.eod_close (or wo.complete) for end-of-day updates."
          extra={
            <Button type="primary" onClick={() => navigate('/mfg/work-order-operations')}>
              Back to WO operations
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <Row justify="space-between" align="middle" gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col flex="auto">
          <Space align="start">
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/mfg/work-order-operations')} />
            <div>
              <Title level={3} style={{ margin: 0 }}>
                End of day register
              </Title>
              <Text type="secondary">
                One row per operation, any work order. Fill the rows you worked on today and save them together.
                Completed operations (closed work orders too) take back entries for the work log.
              </Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Space size={4}>
              <Text type="secondary">Work date</Text>
              <DatePicker
                allowClear={false}
                value={dayjs(workDate)}
                status={workDateError ? 'error' : undefined}
                disabledDate={(d) => d.isAfter(dayjs(), 'day')}
                onChange={(d) => d && setWorkDate(d.format('YYYY-MM-DD'))}
              />
            </Space>
            <Button icon={<ReloadOutlined />} onClick={() => void refetch()} loading={isFetching}>
              Refresh
            </Button>
            <Button icon={<ClearOutlined />} disabled={dirtyRows.length === 0} onClick={discardEdits}>
              Discard
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              disabled={dirtyRows.length === 0 || closeMutation.isPending}
              loading={closeMutation.isPending}
              onClick={() => void save()}
            >
              Save {dirtyRows.length || ''} row{dirtyRows.length === 1 ? '' : 's'}
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[8, 8]} style={{ marginBottom: 8 }}>
        <Col xs={24} md={6}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search WO / operation / item"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>
        <Col xs={12} md={4}>
          <Select allowClear showSearch optionFilterProp="label" placeholder="Work order" style={{ width: '100%' }} value={woFilter} onChange={setWoFilter} options={woOptions} />
        </Col>
        <Col xs={12} md={4}>
          <Select allowClear showSearch optionFilterProp="label" placeholder="Workstation" style={{ width: '100%' }} value={wsFilter} onChange={setWsFilter} options={workstationOptions} />
        </Col>
        <Col xs={12} md={4}>
          <Select allowClear showSearch virtual optionFilterProp="label" placeholder="Operator" style={{ width: '100%' }} value={opFilter} onChange={setOpFilter} options={userOptions} />
        </Col>
        <Col xs={12} md={3}>
          <Select
            allowClear
            placeholder="Status"
            style={{ width: '100%' }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={STATUS_FILTER_OPTIONS}
          />
        </Col>
        <Col xs={24} md={3} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Switch size="small" checked={editedOnly} onChange={setEditedOnly} />
          <Text>Edited only</Text>
        </Col>
      </Row>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 8,
          padding: 8,
          marginBottom: 8,
          borderRadius: 8,
          background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.03))',
        }}
      >
        <Text strong>{selectedIds.length > 0 ? `${selectedIds.length} selected:` : 'Bulk (tick rows):'}</Text>
        <Segmented
          size="small"
          options={[{ label: '—', value: '' }, ...OUTCOME_OPTIONS]}
          value={bulk.outcome ?? ''}
          onChange={(v) => setBulk((b) => ({ ...b, outcome: (v as EodCloseOutcome) || undefined }))}
        />
        <Input size="small" type="time" style={{ width: 100 }} value={bulk.startTime} onChange={(e) => setBulk((b) => ({ ...b, startTime: e.target.value }))} />
        <Input size="small" type="time" style={{ width: 100 }} value={bulk.endTime} onChange={(e) => setBulk((b) => ({ ...b, endTime: e.target.value }))} />
        <Input size="small" inputMode="decimal" placeholder="Hours" style={{ width: 70 }} value={bulk.hours} onChange={(e) => setBulk((b) => ({ ...b, hours: e.target.value }))} />
        <Select size="small" allowClear showSearch virtual optionFilterProp="label" placeholder="Operator" style={{ width: 160 }} value={bulk.operatorId} onChange={(v) => setBulk((b) => ({ ...b, operatorId: v }))} options={userOptions} />
        <Select size="small" allowClear showSearch optionFilterProp="label" placeholder="Workstation" style={{ width: 150 }} value={bulk.workstationId} onChange={(v) => setBulk((b) => ({ ...b, workstationId: v }))} options={workstationOptions} />
        <Input size="small" placeholder="Notes" style={{ width: 180 }} value={bulk.notes} onChange={(e) => setBulk((b) => ({ ...b, notes: e.target.value }))} />
        <Button size="small" type="primary" ghost disabled={selectedIds.length === 0} onClick={applyBulk}>
          Apply
        </Button>
        <Tooltip title={selectedIds.length > 0 ? 'Fill ticked rows from their last saved entry' : 'Fill all untouched rows from their last saved entry'}>
          <Button size="small" icon={<CopyOutlined />} onClick={copyYesterday}>
            Copy yesterday
          </Button>
        </Tooltip>
      </div>

      {workDateError ? <Alert type="warning" showIcon message={workDateError} style={{ marginBottom: 8 }} /> : null}
      {error ? (
        <Alert
          type="error"
          showIcon
          message="Unable to load"
          description={(error as Error).message || 'Failed to load eligible operations'}
          style={{ marginBottom: 8 }}
        />
      ) : null}

      <Table<EodEligibleStage>
        rowKey="id"
        size="small"
        loading={isLoading}
        columns={columns}
        dataSource={filtered}
        scroll={{ x: 1530, y: 'calc(100vh - 360px)' }}
        pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: [25, 50, 100, 200], size: 'small' }}
        rowSelection={{ selectedRowKeys: selectedKeys, onChange: setSelectedKeys, columnWidth: 36 }}
        rowClassName={(s) => (rows[s.id]?.dirty ? 'eod-row-dirty' : '')}
        expandable={{
          rowExpandable: (s) => needsAssignmentForm(s) && (!s.inspector_user_id || !s.verifier_user_id),
          expandedRowRender: (s) => {
            const row = rows[s.id];
            if (!row) return null;
            return (
              <Space wrap>
                <Text type="secondary">Stage roles:</Text>
                <div style={{ width: 200 }}>
                  <SelectCell stageId={s.id} field="inspectorId" value={row.inspectorId} options={userOptions} placeholder="Inspector" onPatch={patchRow} status={row.dirty && !row.inspectorId ? 'error' : undefined} />
                </div>
                <div style={{ width: 200 }}>
                  <SelectCell stageId={s.id} field="verifierId" value={row.verifierId} options={userOptions} placeholder="Verifier" onPatch={patchRow} status={row.dirty && !row.verifierId ? 'error' : undefined} />
                </div>
              </Space>
            );
          },
        }}
        locale={{
          emptyText: <Empty description={stages.length ? 'No rows match the filters' : 'No operations found'} />,
        }}
      />

      <style>{`.eod-row-dirty > td { background: rgba(22,119,255,0.04); }`}</style>

      <EodOperationWorkLogDrawer
        open={!!logStage}
        onClose={() => setLogStage(null)}
        stageId={logStage?.id ?? null}
        title={logStage ? `${logStage.work_order_number || logStage.work_order_id} · ${logStage.stage_code} — ${logStage.stage_name}` : ''}
        fallbackNotes={logStage?.notes ?? null}
        fallbackHours={logStage?.actual_hours ?? null}
        workOrderId={logStage?.work_order_id}
      />
    </div>
  );
};

export default EodClosePage;
